import React, { useState, useEffect } from "react";
import { useAuth, API, formatApiErrorDetail } from "@/App";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { format } from "date-fns";
import { RefreshCw, Trash2, Plus, Loader2, ChevronDown, X, Repeat } from "lucide-react";

const DAYS_MAP = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const RecurringShifts = () => {
  const { getAuthHeaders } = useAuth();
  const [rules, setRules] = useState([]);
  const [positions, setPositions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [generating, setGenerating] = useState(null);
  const [creating, setCreating] = useState(false);
  const [posDropOpen, setPosDropOpen] = useState(false);

  const [form, setForm] = useState({
    event_name: "",
    start_time: "",
    end_time: "",
    positions_needed: [],
    recurrence_type: "weekly",
    days_of_week: [],
    start_date: format(new Date(), "yyyy-MM-dd"),
    end_date: "",
    slots: 1,
  });

  useEffect(() => {
    fetchRules();
    fetchPositions();
  }, []);

  const fetchRules = async () => {
    try {
      const res = await axios.get(`${API}/recurring-shifts`, { headers: getAuthHeaders() });
      setRules(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const fetchPositions = async () => {
    try {
      const res = await axios.get(`${API}/positions`, { headers: getAuthHeaders() });
      setPositions(res.data);
    } catch (e) {
      console.error(e);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.end_date) { toast.error("Please set an end date"); return; }
    if (form.recurrence_type !== "daily" && form.recurrence_type !== "monthly" && form.days_of_week.length === 0) {
      toast.error("Please select at least one day of the week");
      return;
    }
    setCreating(true);
    try {
      await axios.post(`${API}/recurring-shifts`, form, { headers: getAuthHeaders() });
      toast.success("Recurring shift rule created");
      setShowCreate(false);
      setForm({ event_name: "", start_time: "", end_time: "", positions_needed: [], recurrence_type: "weekly", days_of_week: [], start_date: format(new Date(), "yyyy-MM-dd"), end_date: "", slots: 1 });
      fetchRules();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to create rule");
    } finally {
      setCreating(false);
    }
  };

  const handleGenerate = async (ruleId) => {
    setGenerating(ruleId);
    try {
      const res = await axios.post(`${API}/recurring-shifts/${ruleId}/generate`, {}, { headers: getAuthHeaders() });
      toast.success(res.data.message);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to generate shifts");
    } finally {
      setGenerating(null);
    }
  };

  const handleDelete = async (ruleId) => {
    try {
      await axios.delete(`${API}/recurring-shifts/${ruleId}`, { headers: getAuthHeaders() });
      toast.success("Rule deleted");
      fetchRules();
    } catch (err) {
      toast.error("Failed to delete rule");
    }
  };

  const toggleDay = (dayIdx) => {
    setForm(prev => ({
      ...prev,
      days_of_week: prev.days_of_week.includes(dayIdx)
        ? prev.days_of_week.filter(d => d !== dayIdx)
        : [...prev.days_of_week, dayIdx]
    }));
  };

  const togglePosition = (name) => {
    setForm(prev => ({
      ...prev,
      positions_needed: prev.positions_needed.includes(name)
        ? prev.positions_needed.filter(p => p !== name)
        : [...prev.positions_needed, name]
    }));
  };

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="recurring-shifts-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold font-heading text-[#1A1A1A]">Recurring Shifts</h1>
          <p className="text-[#666666]">Manage recurring shift rules and auto-generate shifts</p>
        </div>
        <Button onClick={() => setShowCreate(true)} className="bg-[#6A1E2E] hover:bg-[#521422] text-white" data-testid="create-rule-btn">
          <Plus className="w-4 h-4 mr-2" /> New Rule
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-40"><div className="animate-pulse text-[#666666]">Loading...</div></div>
      ) : rules.length === 0 ? (
        <Card className="border border-[#E5E5E5] shadow-none">
          <CardContent className="py-12 text-center">
            <Repeat className="w-12 h-12 mx-auto text-[#CCCCCC] mb-3" />
            <p className="text-[#666666]">No recurring shift rules yet. Create one to auto-generate shifts.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Event</TableHead>
                <TableHead>Time</TableHead>
                <TableHead>Recurrence</TableHead>
                <TableHead>Days</TableHead>
                <TableHead>Date Range</TableHead>
                <TableHead>Positions</TableHead>
                <TableHead>Slots</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rules.map(rule => (
                <TableRow key={rule.id} data-testid={`rule-row-${rule.id}`}>
                  <TableCell className="font-medium">{rule.event_name}</TableCell>
                  <TableCell className="font-mono text-sm">{rule.start_time}-{rule.end_time}</TableCell>
                  <TableCell>
                    <span className="px-2 py-0.5 text-xs font-medium rounded-sm bg-[#F0EAEB] text-[#6A1E2E] capitalize">{rule.recurrence_type}</span>
                  </TableCell>
                  <TableCell className="text-sm">
                    {rule.recurrence_type === "daily" ? "Every day" :
                     rule.recurrence_type === "monthly" ? `Day ${new Date(rule.start_date + "T00:00:00").getDate()} of month` :
                     (rule.days_of_week || []).map(d => DAYS_MAP[d]).join(", ")}
                  </TableCell>
                  <TableCell className="text-sm">{rule.start_date} to {rule.end_date}</TableCell>
                  <TableCell className="text-sm">{(rule.positions_needed || []).join(", ") || "—"}</TableCell>
                  <TableCell>{rule.slots}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Button
                        size="sm"
                        onClick={() => handleGenerate(rule.id)}
                        disabled={generating === rule.id}
                        className="bg-[#6A1E2E] hover:bg-[#521422] text-white h-8"
                        data-testid={`generate-${rule.id}`}
                      >
                        {generating === rule.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3 mr-1" />}
                        Generate
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleDelete(rule.id)}
                        className="border-red-300 text-red-600 hover:bg-red-50 h-8"
                        data-testid={`delete-rule-${rule.id}`}
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Create Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Repeat className="w-5 h-5 text-[#6A1E2E]" /> New Recurring Shift Rule
            </DialogTitle>
            <DialogDescription>Define a pattern and generate shifts automatically.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4 mt-2" data-testid="create-rule-form">
            <div className="space-y-2">
              <Label>Event Name</Label>
              <Input value={form.event_name} onChange={e => setForm(p => ({ ...p, event_name: e.target.value }))} required className="border-[#E5E5E5]" data-testid="rule-event-name" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Start Time</Label>
                <Input type="time" value={form.start_time} onChange={e => setForm(p => ({ ...p, start_time: e.target.value }))} required className="border-[#E5E5E5] font-mono" data-testid="rule-start-time" />
              </div>
              <div className="space-y-2">
                <Label>End Time</Label>
                <Input type="time" value={form.end_time} onChange={e => setForm(p => ({ ...p, end_time: e.target.value }))} required className="border-[#E5E5E5] font-mono" data-testid="rule-end-time" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Recurrence</Label>
              <Select value={form.recurrence_type} onValueChange={v => setForm(p => ({ ...p, recurrence_type: v }))}>
                <SelectTrigger className="border-[#E5E5E5]" data-testid="rule-recurrence">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="daily">Daily</SelectItem>
                  <SelectItem value="weekly">Weekly</SelectItem>
                  <SelectItem value="biweekly">Bi-weekly</SelectItem>
                  <SelectItem value="monthly">Monthly</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {(form.recurrence_type === "weekly" || form.recurrence_type === "biweekly") && (
              <div className="space-y-2">
                <Label>Days of Week</Label>
                <div className="flex gap-2">
                  {DAYS_MAP.map((day, idx) => (
                    <button
                      key={day}
                      type="button"
                      onClick={() => toggleDay(idx)}
                      className={`w-10 h-10 rounded-sm text-xs font-semibold border transition-colors ${
                        form.days_of_week.includes(idx)
                          ? "bg-[#6A1E2E] text-white border-[#6A1E2E]"
                          : "bg-white text-[#666666] border-[#E5E5E5] hover:border-[#6A1E2E]"
                      }`}
                      data-testid={`rule-day-${day.toLowerCase()}`}
                    >
                      {day}
                    </button>
                  ))}
                </div>
              </div>
            )}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Start Date</Label>
                <Input type="date" value={form.start_date} onChange={e => setForm(p => ({ ...p, start_date: e.target.value }))} required className="border-[#E5E5E5]" data-testid="rule-start-date" />
              </div>
              <div className="space-y-2">
                <Label>End Date</Label>
                <Input type="date" value={form.end_date} onChange={e => setForm(p => ({ ...p, end_date: e.target.value }))} required className="border-[#E5E5E5]" data-testid="rule-end-date" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Positions</Label>
              <DropdownMenu open={posDropOpen} onOpenChange={setPosDropOpen}>
                <DropdownMenuTrigger asChild>
                  <Button type="button" variant="outline" className="w-full justify-between border-[#E5E5E5]">
                    <span className="text-[#666666]">{form.positions_needed.length === 0 ? "Select positions..." : `${form.positions_needed.length} selected`}</span>
                    <ChevronDown className="w-4 h-4 text-[#999999]" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-[280px]">
                  {positions.map(pos => (
                    <DropdownMenuCheckboxItem key={pos.id} checked={form.positions_needed.includes(pos.name)} onCheckedChange={() => togglePosition(pos.name)}>{pos.name}</DropdownMenuCheckboxItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
              {form.positions_needed.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-1">
                  {form.positions_needed.map(p => (
                    <span key={p} className="inline-flex items-center gap-1 px-2 py-0.5 bg-[#6A1E2E] text-white text-xs rounded-sm">
                      {p}
                      <button type="button" onClick={() => togglePosition(p)} className="hover:bg-white/20 rounded-full p-0.5"><X className="w-3 h-3" /></button>
                    </span>
                  ))}
                </div>
              )}
            </div>
            <div className="space-y-2">
              <Label>Slots per Shift</Label>
              <Input type="number" min="1" max="20" value={form.slots} onChange={e => setForm(p => ({ ...p, slots: parseInt(e.target.value) || 1 }))} className="border-[#E5E5E5] w-24" data-testid="rule-slots" />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowCreate(false)} className="border-[#E5E5E5]">Cancel</Button>
              <Button type="submit" disabled={creating} className="bg-[#6A1E2E] hover:bg-[#521422] text-white" data-testid="submit-rule">
                {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create Rule"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RecurringShifts;
